/**
 * Esc closes the topmost open dialog — on every screen, in every module.
 *
 * WHY THIS EXISTS
 *
 * Vuetify already handles Esc, but only for dialogs that are NOT `persistent`.
 * Most of this app's dialogs are persistent on purpose — a stray backdrop click
 * must not discard a part-filled form — and `persistent` switches off Esc as
 * well as backdrop-close. So Esc silently did nothing across most of the app.
 *
 * WHY IT IS BOUND TO THE DOCUMENT
 *
 * The obvious fix is `@keydown.esc` on each dialog. That is the wrong fix, and
 * this codebase already learned it the hard way — see the note at the top of
 * components/storage/StorageFilePicker.vue. A handler on the dialog only fires
 * while focus is already INSIDE it, and focus is not reliably there: during a
 * loading spinner nothing focusable has rendered yet, and clicking a list, a
 * table row or the backdrop leaves focus on <body>. The result is Esc working
 * only some of the time, which is precisely the bug QA reported.
 *
 * One document-level listener has none of that dependence on focus, and it
 * cannot drift out of step the way 200-odd copies of an attribute would.
 *
 * OPTING OUT
 *
 * A dialog that must survive Esc — or that needs its own conditions, like not
 * closing mid-upload — marks itself with `data-esc-close="off"` and handles the
 * key itself. StorageFilePicker does exactly this.
 *
 * HANDLING ESC YOURSELF, WITHOUT GIVING UP THE FOCUS FIX
 *
 * `data-esc-close="off"` is all-or-nothing: the dialog is on its own, and the
 * only tools left are `@keydown.esc` (focus-dependent, the broken thing) or its
 * own document listener plus its own copy of the topmost/nested-overlay rules
 * below. ConfirmDialog pays that price because it owes its caller a promise.
 *
 * A dialog that just needs to run something before it closes — an unsaved-
 * changes prompt, most of them — marks itself `data-esc-close="self"` instead.
 * Esc is then DELIVERED to it and nothing more: the key is re-fired at the
 * dialog's own element, so its `@keydown.esc` handler runs no matter where
 * focus actually was, and this plugin does not close it. Closing is the
 * handler's business. Projects' Edit dialog is the first of these.
 */

const OPT_OUT = '[data-esc-close="off"]';
const SELF = '[data-esc-close="self"]';

/** Overlays that should swallow Esc before any dialog sees it. */
const NESTED = '.v-menu.v-overlay--active, .v-overlay--active.v-autocomplete__content, .v-overlay--active.v-select__content';

function zIndexOf(el: HTMLElement): number {
  const z = Number(window.getComputedStyle(el).zIndex);
  return Number.isFinite(z) ? z : 0;
}

/**
 * Re-fire Esc at the dialog itself.
 *
 * This is the whole focus fix for a `data-esc-close="self"` dialog, and it is
 * deliberately plain DOM: `@keydown.esc` on a <v-dialog> becomes a real
 * listener on the `.v-overlay` root (Vuetify merges fallthrough attrs there),
 * so dispatching the key AT that element runs the handler whatever had focus.
 *
 * `bubbles: false` on purpose. The event is dispatched on the element that
 * carries the listener, so the target phase alone is enough — and a bubbling
 * copy would climb straight back to this document handler and to VOverlay's
 * own window handler, re-entering the first and bouncing the dialog in the
 * second.
 */
function deliverEsc(el: HTMLElement): void {
  el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: false, cancelable: true }));
}

/**
 * Walk up from the overlay element to the VDialog component instance.
 * `__vueParentComponent` is how Vue exposes the owning component on a DOM node
 * — the same handle devtools uses.
 *
 * ONLY IN DEV. Vue defines it behind `__DEV__ || __VUE_PROD_DEVTOOLS__`
 * (runtime-core, mountElement), and neither is on in a `vite build`, so on
 * every deployed environment this returns null and the close below never runs.
 * That is why the force-close path cannot be what a dialog relies on, and why
 * `data-esc-close="self"` above is plain DOM instead.
 */
function findDialogInstance(el: HTMLElement): any | null {
  let inst = (el as any).__vueParentComponent ?? null;
  for (let hops = 0; inst && hops < 12; hops++) {
    const name = inst.type?.name ?? inst.type?.__name;
    if (name === 'VDialog') return inst;
    inst = inst.parent;
  }
  return null;
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.defaultPrevented) return;

  // A nested menu/select renders OUTSIDE the dialog's DOM, so without this one
  // Esc would shut the whole dialog when the user only meant to dismiss the
  // dropdown they had open.
  if (document.querySelector(NESTED)) return;

  const open = Array.from(
    document.querySelectorAll<HTMLElement>('.v-overlay.v-dialog.v-overlay--active')
  );
  if (!open.length) return;

  // Topmost wins — dialogs stack, and Esc should peel one layer at a time.
  const top = open.reduce((a, b) => (zIndexOf(b) >= zIndexOf(a) ? b : a));

  if (top.matches(OPT_OUT) || top.querySelector(OPT_OUT)) return;

  // The dialog has its own close path. Hand it the key and stop — closing it
  // here as well would tear it down underneath the prompt it just opened.
  if (top.matches(SELF)) {
    event.stopPropagation();
    // Only when the key did NOT already pass through the dialog. Focus is
    // sometimes inside — the user clicked a field before hitting Esc — and
    // then the dialog's own listener has had this very event on the way up to
    // here. Delivering a copy as well would run the handler twice.
    if (!top.contains(event.target as Node)) deliverEsc(top);
    return;
  }

  const inst = findDialogInstance(top);
  if (!inst) return;

  // Non-persistent dialogs are Vuetify's own job; closing them here as well
  // would be harmless but redundant, and skipping keeps the behaviour in one
  // place per dialog.
  if (!inst.props?.persistent) return;

  event.stopPropagation();
  inst.emit('update:modelValue', false);
}

export default {
  install() {
    document.addEventListener('keydown', onKeydown);
  }
};
